import React, { useState, useEffect, useCallback, useMemo, useRef } from 'react';
import { Select, MenuItem, SelectChangeEvent } from '@mui/material';
import { Block } from '../classes/Block';
import type { ApiBlock as ApiBlockType } from '../utils/apiTypes';
import { calculateVideoDimensions, VideoDimensions, VideoMode, VideoSettings } from './VideoSettings';
import { t } from '@lingui/core/macro';
import { getApiUrl } from '../utils/api';
import { authFetch } from '../utils/authFetch';
import { useProject } from '../contexts/ProjectContext';
import LogoBlockConfig from './LogoBlockConfig';
import { CardGroup, getBlockCardGroup } from '../utils/cardGroups';
import { CardFramingSettings } from '../utils/cardFraming';
import { toFontUrlMap } from '../utils/fontHelpers';
import { transformBlockToApiFormat } from '../utils/renderPayload';
import { buildSbsPairLookup } from './SBSBlockTabs';

interface HtmlPreviewProps {
  blocks: Block[];
  actualSelectedBlockIndex: number;
  videoSettings: VideoSettings;
  columnDefs: any[];
  onBlockChange: (blockIndex: number, updatedBlock: Block) => void;
  onPreviewFocusedBlock?: (blockIndex: number) => void;
  scrollToBlockCounter?: number;
  cardGroups?: CardGroup[];
}

export type PreviewMode = 'single' | 'surrounding' | 'full';

type ApiBlock = ApiBlockType;
const FOCUS_SYNC_SUPPRESSION_MS: number = 350;

interface PreviewFocusMessage {
  type?: string;
  blockName?: string;
  blockIndex?: number;
  blockId?: string;
  reason?: 'load' | 'scroll';
}

interface PendingFullRestore {
  blockId: string | null;
  blockIndex: number;
}

interface PreviewStateRef {
  selectedBlock: Block | undefined;
  selectedBlockIndex: number;
  effectivePreviewMode: PreviewMode;
}

interface ApiPayload {
  video_settings: {
    background_color: string;
    fps_num: number;
    resolution: string;
    transparent: boolean;
    video_time: number;
    aspect_ratio: string;
    fonts: string | undefined;
    logos: string | undefined;
    mode: VideoMode;
    card_framing?: CardFramingSettings;
  };
  data: ApiBlock[];
}

function getBlocksForSelectedCard(blocks: Block[], cardGroups: CardGroup[], selectedIndex: number): Block[] {
  if (selectedIndex < 0 || selectedIndex >= blocks.length) return [];
  const group: CardGroup | undefined = getBlockCardGroup(cardGroups, blocks[selectedIndex].id);
  if (!group) return [blocks[selectedIndex]];
  return blocks.filter((b: Block) => group.blockIds.includes(b.id));
}

export function getBlocksForPreviewScope(
  blocks: Block[],
  cardGroups: CardGroup[],
  selectedIndex: number,
  previewMode: PreviewMode,
  videoMode: VideoMode
): Block[] {
  if (!blocks?.length || selectedIndex < 0 || selectedIndex >= blocks.length) return [];

  if (videoMode === 'cards') {
    return getBlocksForSelectedCard(blocks, cardGroups, selectedIndex);
  }

  if (previewMode === 'full') return blocks;

  const sbsPairLookup = buildSbsPairLookup(blocks);
  const selectedPair = sbsPairLookup.byIndex.get(selectedIndex)?.pair;
  const selectedStartIndex: number = selectedPair?.startIndex ?? selectedIndex;
  const selectedEndIndex: number = selectedPair?.endIndex ?? selectedIndex;
  const previewBlockIndexes: Set<number> = new Set<number>();

  const addBlockUnit = (blockIndex: number): void => {
    if (blockIndex < 0 || blockIndex >= blocks.length) return;

    const pair = sbsPairLookup.byIndex.get(blockIndex)?.pair;
    const startIndex: number = pair?.startIndex ?? blockIndex;
    const endIndex: number = pair?.endIndex ?? blockIndex;

    for (let index = startIndex; index <= endIndex; index += 1) {
      previewBlockIndexes.add(index);
    }
  };

  addBlockUnit(selectedIndex);

  if (previewMode === 'surrounding') {
    addBlockUnit(selectedStartIndex - 1);
    addBlockUnit(selectedEndIndex + 1);
  }

  return Array.from(previewBlockIndexes)
    .sort((leftIndex: number, rightIndex: number) => leftIndex - rightIndex)
    .map((blockIndex: number) => blocks[blockIndex]);
}

const HtmlPreview: React.FC<HtmlPreviewProps> = ({
  blocks,
  actualSelectedBlockIndex,
  videoSettings,
  columnDefs,
  onBlockChange,
  onPreviewFocusedBlock,
  scrollToBlockCounter,
  cardGroups = [],
}) => {
  const { selectedProject } = useProject();
  const selectedProjectId: string | undefined = selectedProject?.id;
  const [htmlContent, setHtmlContent] = useState<string>('');
  const [loading, setLoading] = useState<boolean>(false);
  const [error, setError] = useState<string | null>(null);
  const [iframeHeight, setIframeHeight] = useState<number>(0);
  const previewContainerRef = useRef<HTMLDivElement | null>(null);
  const savedScrollRef = useRef<number>(0);
  const iframeRef = useRef<HTMLIFrameElement | null>(null);
  const savedIframeScrollRef = useRef<number>(0);
  const iframeScrollHandlerRef = useRef<((event: Event) => void) | null>(null);
  const focusSyncSuppressedRef = useRef<boolean>(false);
  const focusSyncTimerRef = useRef<ReturnType<typeof setTimeout> | null>(null);
  const pendingRestoreRef = useRef<PendingFullRestore | null>(null);

  const [previewModeState, setPreviewModeState] = useState<PreviewMode>(() => {
    const saved: string | null = localStorage.getItem('fintitles_previewMode');
    return (saved === 'single' || saved === 'surrounding' || saved === 'full') ? saved : 'surrounding';
  });

  const [zoom, setZoomState] = useState<number>(() => {
    const saved: string | null = localStorage.getItem('fintitles_zoom');
    return saved !== null ? parseFloat(saved) : 1;
  });

  const [fitToView, setFitToViewState] = useState<boolean>(() => {
    const saved: string | null = localStorage.getItem('fintitles_fitToView');
    return saved !== null ? saved === 'true' : true;
  });

  const [fitScale, setFitScale] = useState<number>(1);

  const setPreviewMode = (value: PreviewMode): void => {
    setPreviewModeState(value);
    localStorage.setItem('fintitles_previewMode', value);
  };

  const setZoom = (value: number): void => {
    setZoomState(value);
    localStorage.setItem('fintitles_zoom', value.toString());
  };

  const setFitToView = (value: boolean): void => {
    setFitToViewState(value);
    localStorage.setItem('fintitles_fitToView', value ? 'true' : 'false');
  };

  // In cards mode, full-roller features (block-focus messaging, scroll sync) are irrelevant
  const effectivePreviewMode: PreviewMode = videoSettings.mode === 'cards' ? 'single' : previewModeState;

  const videoDimensions: VideoDimensions = useMemo(() => (
    calculateVideoDimensions({
      resolution: videoSettings?.resolution ?? 'HD',
      aspect_ratio: videoSettings?.aspect_ratio ?? '16:9',
    })
  ), [videoSettings?.aspect_ratio, videoSettings?.resolution]);
  const originalContentHeight: number = videoDimensions.height;

  const selectedBlock: Block | undefined = blocks[actualSelectedBlockIndex];
  const previewStateRef = useRef<PreviewStateRef>({
    selectedBlock,
    selectedBlockIndex: actualSelectedBlockIndex,
    effectivePreviewMode,
  });

  useEffect(() => {
    previewStateRef.current = {
      selectedBlock,
      selectedBlockIndex: actualSelectedBlockIndex,
      effectivePreviewMode,
    };
  }, [actualSelectedBlockIndex, effectivePreviewMode, selectedBlock]);

  const suppressFocusSync = useCallback((durationMs: number = FOCUS_SYNC_SUPPRESSION_MS): void => {
    if (focusSyncTimerRef.current) {
      clearTimeout(focusSyncTimerRef.current);
    }
    focusSyncSuppressedRef.current = true;
    focusSyncTimerRef.current = setTimeout(() => {
      focusSyncSuppressedRef.current = false;
      focusSyncTimerRef.current = null;
    }, durationMs);
  }, []);

  const findPreviewBlockMarker = useCallback((
    doc: Document,
    identity: { blockId?: string | null; blockIndex?: number | null }
  ): HTMLElement | null => {
    const blockMarkers: HTMLElement[] = Array.from(
      doc.querySelectorAll<HTMLElement>('[data-block-index]')
    );

    if (identity.blockId) {
      const markerById: HTMLElement | undefined = blockMarkers.find(
        (marker: HTMLElement) => marker.getAttribute('data-block-id') === identity.blockId
      );
      if (markerById) return markerById;
    }

    if (identity.blockIndex !== null && identity.blockIndex !== undefined && identity.blockIndex >= 0) {
      const markerByIndex: HTMLElement | undefined = blockMarkers.find((marker: HTMLElement) => {
        return Number(marker.getAttribute('data-block-index')) === identity.blockIndex;
      });
      if (markerByIndex) return markerByIndex;
    }

    return null;
  }, []);

  const captureFullPreviewSelectionSnapshot = useCallback((): void => {
    const currentSelectedBlock: Block | undefined = previewStateRef.current.selectedBlock;
    const currentSelectedBlockIndex: number = previewStateRef.current.selectedBlockIndex;
    const currentEffectivePreviewMode: PreviewMode = previewStateRef.current.effectivePreviewMode;

    if (currentEffectivePreviewMode !== 'full' || !currentSelectedBlock) {
      pendingRestoreRef.current = null;
      return;
    }

    pendingRestoreRef.current = {
      blockId: currentSelectedBlock.id || null,
      blockIndex: currentSelectedBlockIndex,
    };
  }, []);

  const restoreSelectedBlockInFullPreview = useCallback((iframeWindow: Window, iframeDoc: Document): boolean => {
    const currentSelectedBlock: Block | undefined = previewStateRef.current.selectedBlock;
    const currentSelectedBlockIndex: number = previewStateRef.current.selectedBlockIndex;
    const snapshot: PendingFullRestore | null = pendingRestoreRef.current ?? (
      currentSelectedBlock
        ? {
            blockId: currentSelectedBlock.id || null,
            blockIndex: currentSelectedBlockIndex,
          }
        : null
    );
    if (!snapshot) return false;

    const marker: HTMLElement | null = findPreviewBlockMarker(iframeDoc, {
      blockId: snapshot.blockId,
      blockIndex: snapshot.blockIndex,
    });
    pendingRestoreRef.current = null;

    if (!marker) {
      return false;
    }

    suppressFocusSync();
    marker.scrollIntoView({ block: 'center', behavior: 'auto' });
    savedIframeScrollRef.current = iframeWindow.scrollY || iframeWindow.document?.documentElement?.scrollTop || 0;
    return true;
  }, [findPreviewBlockMarker, suppressFocusSync]);

  // Detach iframe scroll listener on unmount
  useEffect(() => {
    return () => {
      const iframeWindow: Window | null = iframeRef.current?.contentWindow ?? null;
      if (iframeWindow && iframeScrollHandlerRef.current) {
        iframeWindow.removeEventListener('scroll', iframeScrollHandlerRef.current);
        iframeScrollHandlerRef.current = null;
      }
      if (focusSyncTimerRef.current) {
        clearTimeout(focusSyncTimerRef.current);
        focusSyncTimerRef.current = null;
      }
      focusSyncSuppressedRef.current = false;
    };
  }, []);

  // Listen for block-focus messages from iframe (Full Roller only)
  useEffect(() => {
    if (effectivePreviewMode !== 'full') return;

    const handleMessage = (event: MessageEvent): void => {
      if (focusSyncSuppressedRef.current) return;
      if (event.source !== iframeRef.current?.contentWindow) return;

      const msg = event.data as PreviewFocusMessage | null;
      if (!msg || msg.type !== 'preview:block-focus') return;
      if (msg.reason !== 'scroll') return;

      const resolvedIndex: number = (typeof msg.blockId === 'string' && msg.blockId.trim() !== '')
        ? blocks.findIndex((b: Block) => b.id === msg.blockId)
        : (typeof msg.blockName === 'string' && msg.blockName.trim() !== '')
          ? blocks.findIndex((b: Block) => b.name === msg.blockName)
          : (Number.isInteger(msg.blockIndex) ? msg.blockIndex! : -1);

      if (resolvedIndex >= 0 && resolvedIndex < blocks.length && resolvedIndex !== actualSelectedBlockIndex) {
        onPreviewFocusedBlock?.(resolvedIndex);
      }
    };

    window.addEventListener('message', handleMessage);
    return () => window.removeEventListener('message', handleMessage);
  }, [actualSelectedBlockIndex, blocks, onPreviewFocusedBlock, effectivePreviewMode]);

  // Scroll iframe to selected block when user clicks a tab (Full Roller only)
  useEffect(() => {
    if (effectivePreviewMode !== 'full' || !scrollToBlockCounter) return;

    const iframeWin: Window | null = iframeRef.current?.contentWindow ?? null;
    const iframeDoc: Document | null = iframeRef.current?.contentDocument ?? null;
    if (!iframeWin || !iframeDoc) return;

    const targetElement: HTMLElement | null = findPreviewBlockMarker(iframeDoc, {
      blockId: blocks[actualSelectedBlockIndex]?.id ?? null,
      blockIndex: actualSelectedBlockIndex,
    });
    if (!targetElement) return;

    suppressFocusSync();
    const viewportHeight: number = iframeWin.innerHeight;
    iframeWin.scrollTo({ top: targetElement.offsetTop - (viewportHeight - targetElement.offsetHeight) / 2, behavior: 'auto' });
  }, [actualSelectedBlockIndex, blocks, effectivePreviewMode, findPreviewBlockMarker, scrollToBlockCounter, suppressFocusSync]);

  const handleIframeLoad = useCallback((event: React.SyntheticEvent<HTMLIFrameElement>): void => {
    const iframe: HTMLIFrameElement = event.currentTarget;
    iframeRef.current = iframe;

    // Re-attach scroll listener (removes any previous handler first)
    const iframeWindow: Window | null = iframe.contentWindow;
    if (iframeWindow) {
      if (iframeScrollHandlerRef.current) {
        iframeWindow.removeEventListener('scroll', iframeScrollHandlerRef.current);
      }
      const handler = (scrollEvent: Event) => {
        const targetDoc = (scrollEvent.target as Document) ?? iframeWindow.document;
        savedIframeScrollRef.current =
          targetDoc?.documentElement?.scrollTop ||
          targetDoc?.body?.scrollTop ||
          iframeWindow.scrollY ||
          0;
      };
      iframeScrollHandlerRef.current = handler;
      iframeWindow.addEventListener('scroll', handler, { passive: true });
    }

    try {
      const iframeDoc: Document | null = iframe.contentDocument || iframe.contentWindow?.document || null;
      if (iframeDoc?.body) {
        const contentHeight: number = iframeDoc.body.scrollHeight;
        setIframeHeight(Math.max(Math.min(contentHeight, originalContentHeight), originalContentHeight));
        requestAnimationFrame(() => {
          const container: HTMLDivElement | null = previewContainerRef.current;
          if (container) {
            const maxScroll: number = container.scrollHeight - container.clientHeight;
            container.scrollTo({ top: Math.min(savedScrollRef.current, Math.max(0, maxScroll)) });
          }
          const win: Window | null = iframeRef.current?.contentWindow ?? null;
          if (win) {
            const currentIframeDoc: Document | null = iframeRef.current?.contentDocument ?? win.document ?? null;
            const restoredFullPreviewSelection: boolean =
              effectivePreviewMode === 'full' && currentIframeDoc
                ? restoreSelectedBlockInFullPreview(win, currentIframeDoc)
                : false;

            if (!restoredFullPreviewSelection) {
              const docEl = win.document?.documentElement;
              const body = win.document?.body;
              const maxIframeScroll: number =
                Math.max(docEl?.scrollHeight ?? 0, body?.scrollHeight ?? 0) -
                (iframeRef.current?.clientHeight ?? 0);
              win.scrollTo({ top: Math.min(savedIframeScrollRef.current, Math.max(0, maxIframeScroll)) });
            }
          }
        });
      }
    } catch {
      setIframeHeight(originalContentHeight);
    }
  }, [effectivePreviewMode, originalContentHeight, restoreSelectedBlockInFullPreview]);

  const blocksForPreview: Block[] = useMemo((): Block[] => {
    return getBlocksForPreviewScope(
      blocks,
      cardGroups,
      actualSelectedBlockIndex,
      previewModeState,
      videoSettings.mode
    );
  }, [blocks, actualSelectedBlockIndex, previewModeState, videoSettings.mode, cardGroups]);

  useEffect(() => {
    const generateHtml = async (): Promise<void> => {
      if (!blocksForPreview.length || !columnDefs.length) {
        setHtmlContent('');
        setError(null);
        return;
      }
      if (!selectedProjectId) {
        setHtmlContent('');
        setError('No project selected');
        return;
      }

      captureFullPreviewSelectionSnapshot();
      setLoading(true);
      setError(null);
      setIframeHeight(0);

      try {
        const apiBlocks: ApiBlock[] = blocksForPreview.map((block: Block): ApiBlock => (
          transformBlockToApiFormat(block, {
            columnDefs,
            propertiesMode: 'preview',
            includeId: true,
          })
        ));

        const payload: ApiPayload = {
          video_settings: {
            background_color: videoSettings.background_color,
            fps_num: videoSettings.fps_num,
            resolution: videoSettings.resolution,
            transparent: videoSettings.transparent,
            video_time: videoSettings.video_time,
            aspect_ratio: videoSettings.aspect_ratio,
            fonts: JSON.stringify(toFontUrlMap(videoSettings.fonts)),
            logos: JSON.stringify(videoSettings.logos),
            mode: videoSettings.mode,
            ...(videoSettings.mode === 'cards' ? { card_framing: videoSettings.card_framing } : {}),
          },
          data: apiBlocks,
        };

        const response = await authFetch(`${getApiUrl()}/html-preview?projectId=${selectedProjectId}`, {
          method: 'POST',
          headers: { 'Content-Type': 'application/json' },
          body: JSON.stringify(payload),
        });

        if (!response.ok) throw new Error(`HTTP error! status: ${response.status}`);

        const result = await response.json();
        if (result.status === 'OK') {
          setHtmlContent(result.html || '');
        } else {
          setError(result.message || 'Unknown error occurred');
          setHtmlContent('');
        }
      } catch (err) {
        setError(err instanceof Error ? err.message : 'Failed to generate HTML preview');
        setHtmlContent('');
      } finally {
        setLoading(false);
      }
    };

    generateHtml();
  }, [blocksForPreview, captureFullPreviewSelectionSnapshot, videoSettings, columnDefs, selectedProjectId]);

  const previewWidth: number = videoDimensions.width;
  const frameHeight: number = iframeHeight > 0 ? iframeHeight : originalContentHeight;
  const isCardsMode: boolean = videoSettings.mode === 'cards';
  const fitActive: boolean = fitToView;
  const effectiveScale: number = fitActive ? fitScale : zoom;
  const scaledPreviewWidth: number = previewWidth * effectiveScale;
  const scaledFrameHeight: number = frameHeight * effectiveScale;

  const updateFitScale = useCallback((): void => {
    if (!fitActive) return;
    const container: HTMLDivElement | null = previewContainerRef.current;
    if (!container || previewWidth <= 0 || (isCardsMode && frameHeight <= 0)) { setFitScale(1); return; }
    const containerStyle: CSSStyleDeclaration = window.getComputedStyle(container);
    const horizontalPadding: number =
      parseFloat(containerStyle.paddingLeft || '0') + parseFloat(containerStyle.paddingRight || '0');
    const verticalPadding: number =
      parseFloat(containerStyle.paddingTop || '0') + parseFloat(containerStyle.paddingBottom || '0');
    const availableWidth: number = container.clientWidth - horizontalPadding;
    const availableHeight: number = container.clientHeight - verticalPadding;
    if (availableWidth <= 0 || (isCardsMode && availableHeight <= 0)) { setFitScale(1); return; }
    const widthScale: number = availableWidth / previewWidth;
    const baseScale: number = isCardsMode
      ? Math.min(widthScale, availableHeight / frameHeight, 1)
      : Math.min(widthScale, 1);
    setFitScale(baseScale);
  }, [fitActive, frameHeight, isCardsMode, previewWidth]);

  useEffect(() => {
    if (!fitActive) { setFitScale(1); return; }
    updateFitScale();
    const container: HTMLDivElement | null = previewContainerRef.current;
    const resizeObserver: ResizeObserver | null = 'ResizeObserver' in window
      ? new ResizeObserver(updateFitScale)
      : null;
    if (container && resizeObserver) {
      resizeObserver.observe(container);
    }
    window.addEventListener('resize', updateFitScale);
    return () => {
      resizeObserver?.disconnect();
      window.removeEventListener('resize', updateFitScale);
    };
  }, [fitActive, updateFitScale, htmlContent, iframeHeight]);

  const previewOptions: { value: PreviewMode; label: string }[] = [
    { value: 'single', label: t`Single Block` },
    { value: 'surrounding', label: t`Surrounding Blocks` },
    { value: 'full', label: t`Full Roller` },
  ];

  return (
    <div id="html-preview" className="html-preview">
      <div
        className="preview-header"
        style={{ display: 'flex', flexDirection: 'column', alignItems: 'center', width: '100%' }}
      >
        <div style={{ display: 'flex', alignItems: 'center', justifyContent: 'center', width: '100%' }}>
          <h3>
            {t`Credits Preview`}
          </h3>
          <div style={{ paddingBottom: '1vh' }} className="tooltip-wrapper">
            <span className="tooltip-icon">?</span>
            <span className="tooltip-text">
              {t`The preview doesn't show the full width of the video (even in fullscreen).`}
            </span>
          </div>
        </div>
      </div>

      {/* Controls OUTSIDE the preview container for better layout */}
      <div className="preview-controls">
        {videoSettings.mode !== 'cards' && (
          <label className="preview-mode-select" style={{ display: 'flex', alignItems: 'center', gap: '10px' }}>
            <span>{t`Preview Scope:`}</span>
            <Select
              value={previewModeState}
              onChange={(e: SelectChangeEvent<string>) => setPreviewMode(e.target.value as PreviewMode)}
              style={{ width: '200px', height: '36px', fontSize: '14px' }}
              sx={{
                '.MuiSelect-select': { textAlign: 'left', display: 'flex', alignItems: 'center', paddingLeft: '10px' },
                '.MuiSelect-icon': { color: '#007CB2' }
              }}
              MenuProps={{ PaperProps: { style: { fontSize: 14 } } }}
            >
              {previewOptions.map((option) => (
                <MenuItem key={option.value} value={option.value}>
                  {option.label}
                </MenuItem>
              ))}
            </Select>
          </label>
        )}
        <div style={{ display: 'flex', alignItems: 'center', gap: '12px', flexWrap: 'wrap' }}>
          <label className="fit-to-view-checkbox" style={{ display: 'flex', alignItems: 'center', gap: '8px', cursor: 'pointer' }}>
            <input
              type="checkbox"
              checked={fitToView}
              onChange={(e) => setFitToView(e.target.checked)}
            />
            <span>{t`Fit to view`}</span>
          </label>
          {!fitToView && (
            <label className="zoom-control" style={{ position: 'relative', display: 'flex', alignItems: 'center', gap: '8px' }}>
              <span>{t`Zoom:`}</span>
              <input
                type="range"
                min={0.05}
                max={1}
                step={0.01}
                value={zoom}
                disabled={fitActive}
                onChange={(e) => setZoom(parseFloat(e.target.value))}
              />
              <span className="zoom-percentage">{Math.round((fitActive ? fitScale : zoom) * 100)}%</span>
              <div className="tooltip-wrapper" style={{ marginLeft: 8 }}>
                <span className="tooltip-icon">?</span>
                <span className="tooltip-text" style={{ height: "70px" }}>
                  {t`This only changes the preview zoom and does not affect the actual video dimensions.`}
                </span>
              </div>
            </label>
          )}
        </div>
      </div>

      {selectedBlock?.isLogoBlock && selectedBlock && (
        <LogoBlockConfig
          block={selectedBlock}
          blockIndex={actualSelectedBlockIndex}
          onBlockChange={(updatedBlock) => onBlockChange(actualSelectedBlockIndex, updatedBlock)}
        />
      )}

      {loading && (
        <div className="preview-loading">
          <p>{t`Generating preview...`}</p>
        </div>
      )}

      {error && (
        <div className="preview-error">
          <p>{t`Error: ${error}`}</p>
        </div>
      )}

      {!loading && !error && (
        <div
          className="preview-container"
          style={{
            width: '100%',
            maxWidth: 'min(100%, calc(100vw - 48px))',
            maxHeight: '84vh',
            overflowX: fitActive ? 'hidden' : 'auto',
            overflowY: isCardsMode && !fitActive ? 'auto' : 'hidden',
            position: 'relative',
            display: 'flex',
            justifyContent: 'center',
            alignItems: 'flex-start',
            boxSizing: 'border-box',
          }}
          ref={previewContainerRef}
          onScroll={(e) => { savedScrollRef.current = e.currentTarget.scrollTop; }}
        >
          {htmlContent ? (
            <div
              style={{
                width: `${scaledPreviewWidth}px`,
                height: `${scaledFrameHeight}px`,
                flex: '0 0 auto',
                position: 'relative',
              }}
            >
              <div
                style={{
                  transform: `scale(${effectiveScale})`,
                  transformOrigin: 'top left',
                  width: `${previewWidth}px`,
                  height: `${frameHeight}px`,
                  position: 'absolute',
                  top: 0,
                  left: 0,
                }}
              >
                <iframe
                  style={{
                    width: `${previewWidth}px`,
                    height: `${frameHeight}px`,
                    border: 'none',
                    pointerEvents: 'auto',
                    display: 'block',
                    backgroundColor: videoSettings.transparent ? 'transparent' : videoSettings.background_color,
                  }}
                  srcDoc={htmlContent}
                  title="HTML Preview"
                  onLoad={handleIframeLoad}
                />
              </div>
            </div>
          ) : (
            <p>{t`No preview available. Please select a block with data.`}</p>
          )}
        </div>
      )}
    </div>
  );
};

export default HtmlPreview;
